import { rm } from 'utils/';
import { useToggle } from 'hooks';
import { Button, LoaderButton } from 'components/Button';
import Modal from 'components/Shared/Modal';
import styles from './styles.scss';
import { Input } from 'components/Shared/Form';
import { postGetCartUrl, postShareCart } from 'services/ApiCheckoutPageService';
import { Reducer, useEffect, useReducer, useRef } from 'react';
import { isValidEmail } from 'components/Shared/Form/rules';

type ModalProps = {
  onClose: () => void;
};

interface OwnState {
  emailLoading: boolean;
  email: string;
  emailError: string;
  url: string;
  urlLoading: boolean;
}

const initialState: OwnState = {
  emailLoading: false,
  email: '',
  emailError: '',
  url: '',
  urlLoading: true
};

type OwnAction =
  | { type: 'emailLoading' }
  | { type: 'email'; value: string }
  | { type: 'emailError'; value: string }
  | { type: 'url'; value: string };

const ownReducer: Reducer<OwnState, OwnAction> = (state, action) => {
  switch (action.type) {
    case 'emailLoading':
      return {
        ...state,
        emailLoading: !state.emailLoading
      };
    case 'email':
      return {
        ...state,
        email: action.value,
        emailError: ''
      };
    case 'emailError':
      return {
        ...state,
        emailError: action.value
      };
    case 'url':
      return {
        ...state,
        url: action.value,
        urlLoading: false
      };
    default:
      return state;
  }
};

const ModalContent = ({ onClose }: ModalProps) => {
  const [state, dispatch] = useReducer(ownReducer, initialState);
  const copyRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void postGetCartUrl().then(response => {
      dispatch({ type: 'url', value: response.url });
    });
  }, []);

  const emailChange = (_e: unknown, value: string) => {
    dispatch({ type: 'email', value });
  };

  const doShare = () => {
    if (!isValidEmail(state.email)) {
      dispatch({ type: 'emailError', value: rm('checkout_sendcart_error') });
      return Promise.resolve();
    }

    dispatch({ type: 'emailLoading' });
    return postShareCart(state.email).then(() => {
      dispatch({ type: 'emailLoading' });
    });
  };

  const doCopy = () => {
    copyRef.current?.select();
    document.execCommand('copy');
    return Promise.resolve();
  };

  return (
    <>
      <h2>{rm('checkout_share_cart')}</h2>
      <div className={styles.wrapper}>
        <label htmlFor="shareEmailInput">{rm('checkout_share_cart_email')}</label>
        <div className={styles.row}>
          <Input
            id="shareEmailInput"
            type="email"
            className={styles.shareCartInput}
            value={state.email}
            onChange={emailChange}
            placeholder={rm('checkout_share_cart_dummy_email')}
          />
          <LoaderButton className={styles.btn} appearance="whitePill" onClick={doShare}>
            {rm('checkout_sendcart_button')}
          </LoaderButton>
        </div>
        {state.emailError && <span className={styles.error}>{state.emailError}</span>}
        <label htmlFor="shareCopyInput">{rm('checkout_share_cart_link')}</label>
        <div className={styles.row}>
          <Input
            id="shareCopyInput"
            className={styles.shareCartInput}
            value={state.url}
            innerRef={copyRef}
            loading={state.urlLoading}
          />
          <Button className={styles.btn} appearance="whitePill" onClick={doCopy}>
            {rm('checkout_copy')}
          </Button>
        </div>
        <Button className={styles.close} appearance="whitePill" onClick={onClose}>
          {rm('shared_button_close')}
        </Button>
      </div>
    </>
  );
};

export const ShareCart = () => {
  const [showShare, toggleShare] = useToggle();

  return (
    <>
      <Modal show={showShare} contentClassName={styles.contentClass}>
        <ModalContent onClose={toggleShare} />
      </Modal>
      <Button onClick={toggleShare} appearance="link" className={styles.share} type="button">
        {rm('checkout_share_cart')}
      </Button>
    </>
  );
};
